import { Component, effect, inject, input, model, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';

import { IconComponent } from '@reforgium/icon';
import { LangPipe, LangService } from '@reforgium/presentia';
import { SkButtonComponent, SkInputTextComponent } from '@ss/ui';

@Component({
  selector: 'app-search-action',
  template: `
    <div class="search-action">
      @let ph = placeholderLang() || '';
      <sk-input-text
        class="input-search"
        [(value)]="searchText"
        [placeholder]="ph.length ? (ph | lang) : ph"
        [ariaLabel]="ph.length ? (ph | lang) : ph"
        (changed)="handleInput($event)"
      >
        <re-icon skInputPrefix width="18" height="18" name="magn-glass" />
      </sk-input-text>
      @if (isShow()) {
        <sk-button
          icon="magn-glass"
          [ariaLabel]="'common.actions.search' | lang"
          [label]="compact() ? undefined : ('common.actions.search' | lang)"
          (clicked)="handleSearch()"
          [loading]="loading()"
        />
      }
    </div>
  `,
  imports: [IconComponent, LangPipe, SkButtonComponent, SkInputTextComponent],
  styles: `
    :host {
      display: block;
      flex: 1 1 auto;
      min-width: 0;
      width: 100%;
    }

    .search-action {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
    }

    .input-search {
      flex: 1 1 auto;
      min-width: 0;
      --sk-input-background: var(--search-glass-background, #ffffff30);
      --sk-input-border: var(--search-glass-border, 1px solid #ffffff30);
      --sk-input-color: #fff;
      --sk-input-placeholder-color: #e2e9f1;
      --sk-input-padding-inline: 12px;
    }

    @media (max-width: 600px) {
      .search-action {
        gap: 0.5rem;
      }
    }
  `,
})
export class SearchAction {
  placeholderLang = input<string | undefined>();
  placeholderRaw = input<string | undefined>();
  parentSearchTextControl = model<FormControl | undefined>();
  loading = input<boolean>(false);
  isShow = input<boolean>(false);
  compact = input(false);
  submitSearch = output<string>();

  private readonly langService = inject(LangService);

  protected readonly ph = signal('');
  protected searchText = signal('');

  constructor() {
    effect((onCleanup) => {
      const control = this.parentSearchTextControl();
      const subscription = control?.valueChanges.subscribe((value) => {
        this.searchText.set(typeof value === 'string' ? value : '');
      });

      this.searchText.set(typeof control?.value === 'string' ? control.value : '');
      onCleanup(() => subscription?.unsubscribe());
    });

    const placeholderKey = this.placeholderLang();
    const placeholder = placeholderKey || this.placeholderRaw() || '';

    if (placeholderKey && /^[a-z][a-z0-9_-]*(?:\.[a-z0-9_-]+)+$/i.test(placeholderKey)) {
      const namespace = placeholderKey.split('.')[0];

      this.langService.loadNamespace(namespace).then(() => this.ph.set(this.langService.get(placeholderKey)));
    } else {
      this.ph.set(placeholder);
    }
  }

  protected handleInput(value: string): void {
    this.searchText.set(value);
    this.parentSearchTextControl()?.setValue(value);
  }

  protected handleSearch(): void {
    const parentValue = this.parentSearchTextControl()?.value;

    this.submitSearch.emit(typeof parentValue === 'string' ? parentValue : this.searchText());
  }
}
